import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth, landingPath } from "@/context/AuthContext";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { setUser, loadExec } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      const ex = await loadExec();
      navigate(landingPath(ex, data));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isPreview = typeof window !== "undefined" && window.location.hostname.includes("preview.emergentagent.com");
  const quickAccounts = [
    { label: "Owner / Admin", sub: "Full admin console", email: "scott.scarboro@gmail.com", password: "MarineAdmin2026!" },
    { label: "Demo Dealer", sub: "Dealer-only workspace", email: "demo@marineadai.com", password: "DealerDemo2026!" },
    { label: "Group Executive", sub: "Black Label Marine Group", email: "exec@blacklabelmarine.com", password: "Executive2026!" },
  ];

  const quickLogin = async (acct) => {
    setError("");
    setEmail(acct.email);
    setPassword(acct.password);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: acct.email, password: acct.password });
      setUser(data);
      const ex = await loadExec();
      navigate(landingPath(ex, data));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to your dealership workspace"
      footer={<>Don't have an account? <Link to="/signup" className="text-gold font-semibold hover:underline" data-testid="go-signup-link">Create one</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Work Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" placeholder="you@dealership.com" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-gold hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" placeholder="••••••••" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-navy hover:bg-navy-light text-white h-11 text-base" data-testid="login-submit-button">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
        </Button>
      </form>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
        <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-3 text-muted-foreground">or</span></div>
      </div>

      <Button type="button" variant="outline" onClick={googleLogin} className="w-full h-11 border-2" data-testid="google-login-button">
        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5 mr-2" />
        Continue with Google
      </Button>

      {isPreview && (
        <div className="mt-6 rounded-xl border border-dashed border-gold/50 bg-gold/5 p-4" data-testid="quick-access-panel">
          <p className="text-xs font-semibold uppercase tracking-wide text-navy/70 mb-3">Quick access (preview only)</p>
          <div className="grid grid-cols-1 gap-2">
            {quickAccounts.map((acct) => (
              <button
                key={acct.email}
                type="button"
                disabled={loading}
                onClick={() => quickLogin(acct)}
                data-testid={`quick-login-${acct.email.split("@")[0].replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                className="flex items-center justify-between rounded-lg border border-navy/10 bg-white px-4 py-3 text-left transition-colors hover:border-gold hover:bg-gold/10 disabled:opacity-60"
              >
                <span>
                  <span className="block text-sm font-semibold text-navy">{acct.label}</span>
                  <span className="block text-xs text-muted-foreground">{acct.sub}</span>
                </span>
                <span className="text-xs font-medium text-gold">Log in &rarr;</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </AuthShell>
  );
}
